import React from 'react';
import '../../features/brand/cwm-tokens.css';

/**
 * Brand-aware skeleton primitive. Use instead of a plain "Loading…"
 * string — skeletons give a much more confident perceived-speed story
 * while async data resolves. Animation is a subtle blue shimmer that
 * matches CWM tokens; reduced-motion users get a flat fill so we don't
 * trigger vestibular issues.
 *
 * Usage:
 *   <Skeleton h={16} w={120} />                   // single line
 *   <Skeleton h={16} w="60%" mb={8} />            // with bottom gap
 *   <SkeletonRow lines={3} />                     // 3-line paragraph
 *   <SkeletonCard />                              // sheet-shaped placeholder
 */

export function Skeleton({
  h = 14,
  w = '100%',
  radius = 8,
  mb = 0,
  style,
}) {
  return (
    <div
      className="cwm-skeleton"
      aria-hidden
      style={{
        height: h, width: w,
        borderRadius: radius,
        marginBottom: mb,
        background:
          'linear-gradient(90deg, var(--cwm-gray-100, #F1F5F9) 0%, #EAF2FB 50%, var(--cwm-gray-100, #F1F5F9) 100%)',
        backgroundSize: '200% 100%',
        animation: 'cwmShimmer 1.4s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

export function SkeletonRow({ lines = 3, h = 12, gap = 8 }) {
  return (
    <div style={{ display: 'grid', gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          h={h}
          w={i === lines - 1 ? '70%' : '100%'}
        />
      ))}
    </div>
  );
}

export function SkeletonCard({ h = 160, radius = 20 }) {
  return (
    <div
      className="cwm-sheet cwm-skeleton"
      aria-hidden
      style={{
        padding: 0,
        height: h,
        borderRadius: radius,
        background:
          'linear-gradient(90deg, #fff 0%, var(--cwm-blue-pale, #EAF4FF) 50%, #fff 100%)',
        backgroundSize: '200% 100%',
        animation: 'cwmShimmer 1.4s ease-in-out infinite',
      }}
    />
  );
}

/**
 * Shared keyframes + reduced-motion override. Rendered once at app root
 * (or via the first Skeleton instance) so callers don't have to.
 */
export function SkeletonStyles() {
  return (
    <style>{`
      @keyframes cwmShimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .cwm-skeleton { animation: none !important; background: var(--cwm-gray-100, #F1F5F9) !important; }
      }
    `}</style>
  );
}
